import { useState, useCallback, useRef } from 'react'
import {
  Title,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Switch,
  Text,
  Alert,
  Loader,
  Center,
  TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconPencil, IconTrash, IconPlus, IconAlertCircle, IconSearch } from '@tabler/icons-react'
import { useTitlesStore } from '../../stores/titlesStore'
import { useClientsStore } from '../../stores/clientsStore'
import { useApplicationsStore } from '../../stores/applicationsStore'
import { Title as TitleType, CreateTitleData, UpdateTitleData } from '../../api/titlesApi'
import { TitleForm } from './TitleForm'

export function TitlesView() {
  const {
    titles,
    total,
    isLoading,
    error,
    duplicateWarning,
    fetchTitles,
    createTitle,
    updateTitle,
    deleteTitle,
    clearError,
    clearDuplicateWarning,
  } = useTitlesStore()

  const { clients, fetchClients } = useClientsStore()
  const { applications, fetchApplications } = useApplicationsStore()

  const [showInactive, setShowInactive] = useState(false)
  const [search, setSearch] = useState('')
  const [formOpened, setFormOpened] = useState(false)
  const [editingTitle, setEditingTitle] = useState<TitleType | null>(null)

  // Build a lookup map for client names
  const clientNameMap = Object.fromEntries(clients.map((c) => [c._id, c.name]))

  const doFetch = useCallback(
    (overrides?: { showInactive?: boolean; search?: string }) => {
      const inactive = overrides?.showInactive ?? showInactive
      const q = overrides?.search ?? search
      const params: Record<string, string> = {}
      if (!inactive) params.isActive = 'true'
      if (q.trim()) params.search = q.trim()
      fetchTitles(params)
    },
    [showInactive, search, fetchTitles]
  )

  // Initial fetch on mount
  const didMount = useRef(false)
  if (!didMount.current) {
    didMount.current = true
    fetchTitles({ isActive: 'true' })
    fetchClients({ isActive: 'true' })
    fetchApplications()
  }

  const handleCreate = () => {
    setEditingTitle(null)
    setFormOpened(true)
  }

  const handleEdit = (title: TitleType) => {
    setEditingTitle(title)
    setFormOpened(true)
  }

  const handleFormSubmit = async (data: CreateTitleData | UpdateTitleData): Promise<boolean> => {
    if (editingTitle) {
      const success = await updateTitle(editingTitle._id, data as UpdateTitleData)
      if (success) {
        notifications.show({ title: 'Success', message: 'Title updated', color: 'green' })
      }
      return success
    } else {
      const result = await createTitle(data as CreateTitleData)
      if (!result) return false
      if (result.created) {
        notifications.show({ title: 'Success', message: 'Title created', color: 'green' })
        return true
      }
      return false
    }
  }

  const handleDelete = (title: TitleType) => {
    modals.openConfirmModal({
      title: 'Deactivate Title',
      children: (
        <Text size="sm">
          Are you sure you want to deactivate <strong>{title.name}</strong>?
        </Text>
      ),
      labels: { confirm: 'Deactivate', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const success = await deleteTitle(title._id)
        if (success) {
          notifications.show({
            title: 'Deactivated',
            message: `${title.name} has been deactivated`,
            color: 'green',
          })
        }
      },
    })
  }

  const rows = titles.map((title) => {
    const clientNames = title.clients
      .map((c) => clientNameMap[c.clientId] ?? c.clientId)
      .join(', ')

    const externalIdCount = Object.keys(title.externalIds ?? {}).length

    return (
      <Table.Tr key={title._id}>
        <Table.Td>
          <Text fw={500}>{title.name}</Text>
          {title.aliases.length > 0 && (
            <Text size="xs" c="dimmed">
              {title.aliases.join(', ')}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{title.releaseYear ?? '—'}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c={clientNames ? undefined : 'dimmed'}>
            {clientNames || '—'}
          </Text>
        </Table.Td>
        <Table.Td>
          {externalIdCount > 0 ? (
            <Badge variant="light" color="gray" size="sm">
              {externalIdCount} ID{externalIdCount !== 1 ? 's' : ''}
            </Badge>
          ) : (
            <Text size="sm" c="dimmed">—</Text>
          )}
        </Table.Td>
        <Table.Td>
          <Badge color={title.isActive ? 'green' : 'gray'} variant="light">
            {title.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Group gap="xs">
            <ActionIcon variant="subtle" onClick={() => handleEdit(title)}>
              <IconPencil size={16} />
            </ActionIcon>
            {title.isActive && (
              <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(title)}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Titles</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
          New Title
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={clearError} withCloseButton>
          {error}
        </Alert>
      )}

      <Group>
        <TextInput
          placeholder="Search titles..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') doFetch({ search: e.currentTarget.value })
          }}
          onBlur={() => doFetch()}
          w={280}
        />
        <Switch
          label="Show inactive"
          checked={showInactive}
          onChange={(e) => {
            const val = e.target.checked
            setShowInactive(val)
            doFetch({ showInactive: val })
          }}
        />
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : titles.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No titles found. Create your first title to get started.
        </Text>
      ) : (
        <>
        {total > titles.length && (
          <Text size="sm" c="dimmed">
            Showing {titles.length} of {total} titles
          </Text>
        )}
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name / Aliases</Table.Th>
              <Table.Th>Year</Table.Th>
              <Table.Th>Clients</Table.Th>
              <Table.Th>External IDs</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
        </>
      )}

      <TitleForm
        opened={formOpened}
        onClose={() => {
          setFormOpened(false)
          clearDuplicateWarning()
        }}
        onSubmit={handleFormSubmit}
        title={editingTitle}
        duplicateWarning={duplicateWarning}
        onClearDuplicateWarning={clearDuplicateWarning}
        clients={clients}
        applications={applications}
      />
    </Stack>
  )
}
